// =============================================================================
// CK — /admin/set-ingestion
// =============================================================================
// Set Ingestion Pipeline · Phase 6B · Editable parallels + summary.
// Reads from Supabase: set_drafts, set_draft_cards, set_draft_parallels.
// Calls three edge functions: pipeline-parse-pdf, pipeline-research-parallels,
// pipeline-draft-summary.
//
// Phase 6A: cards table is inline-editable.
// Phase 6B (this): parallels grouped by sub-set, inline-editable per row.
//   Summary text is also inline-editable (textarea with save-on-blur).
//   All edits write directly to their tables (NOT via edge functions).
//   Auto-flips edited_by_human=true on any parallel edit.
//   Optimistic UI with rollback on error.
// Phase 6C/D: pipeline-publish edge function + PUBLISH button wiring.
//
// Access gated by isAdmin() — same pattern as AdminProductRoadmap.
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { isAdmin } from './adminAuth';
import NavDrawer from './NavDrawer';

const C = {
  bg:           '#04060f',
  card:         '#0a1020',
  cardAlt:      '#0f1828',
  border:       '#1a2540',
  borderSoft:   '#11192c',
  rowBg:        'rgba(8,12,26,0.55)',
  rowBgAlt:     'rgba(15,24,40,0.4)',
  gold:         '#f0c040',
  goldDim:      '#c9a227',
  cyan:         '#00d4ff',
  green:        '#00e87a',
  purple:       '#b060ff',
  red:          '#ff4444',
  ink:          '#e8ecf5',
  inkSoft:      '#7a9ab8',
  inkFaint:     '#4a5a7a',
};
const FONT_DISPLAY = "'Orbitron', sans-serif";
const FONT_BODY    = "'Rajdhani', sans-serif";
const FONT_MONO    = "'Share Tech Mono', monospace";

const STATUS_COLOR = {
  new:        C.inkFaint,
  parsed:     C.cyan,
  researched: C.gold,
  drafted:    C.green,
  published:  C.purple,
};
const STATUS_LABEL = {
  new:        'NEW',
  parsed:     'PARSED',
  researched: 'RESEARCHED',
  drafted:    'DRAFTED',
  published:  'PUBLISHED',
};

function navigateTo(href) {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString();
}

// ============================================================================
// Toast — minimal in-app notification (Phase 6A new)
// ============================================================================

function useToast() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = useCallback((message, kind = 'success') => {
    const id = ++idRef.current;
    setToasts(arr => [...arr, { id, message, kind }]);
    setTimeout(() => {
      setToasts(arr => arr.filter(t => t.id !== id));
    }, kind === 'error' ? 4000 : 1800);
  }, []);

  return { toasts, showToast };
}

function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      pointerEvents: 'none',
    }}>
      {toasts.map(t => {
        const accent = t.kind === 'error' ? C.red : t.kind === 'info' ? C.cyan : C.green;
        return (
          <div key={t.id} style={{
            background: C.cardAlt,
            border: `1px solid ${accent}`,
            borderLeft: `3px solid ${accent}`,
            borderRadius: '4px',
            padding: '10px 14px',
            fontFamily: FONT_MONO,
            fontSize: '11px',
            letterSpacing: '1px',
            color: accent,
            minWidth: '200px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            pointerEvents: 'auto',
          }}>
            {t.message}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================
export default function AdminSetIngestion({ user }) {
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    if (!isAdmin(user)) {
      navigateTo('/');
      return;
    }
    setAuthReady(true);
  }, [user]);

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { toasts, showToast } = useToast();

  const refresh = useCallback(async () => {
    const { data, error } = await supabase
      .from('set_drafts')
      .select('*')
      .order('updated_at', { ascending: false });
    if (error) {
      console.error('drafts fetch', error);
      setDrafts([]);
    } else {
      setDrafts(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authReady) refresh();
  }, [authReady, refresh]);

  const counts = useMemo(() => {
    const c = { total: drafts.length, inProgress: 0, ready: 0, published: 0 };
    for (const d of drafts) {
      if (d.status === 'published') c.published++;
      else if (d.status === 'drafted') c.ready++;
      else if (d.status === 'parsed' || d.status === 'researched') c.inProgress++;
    }
    return c;
  }, [drafts]);

  const selectedDraft = useMemo(
    () => drafts.find(d => d.id === selectedDraftId) || null,
    [drafts, selectedDraftId]
  );

  if (!authReady) return null;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: FONT_BODY, color: C.ink, WebkitFontSmoothing: 'antialiased' }}>
      <NavDrawer user={user} />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 20px 80px' }}>

        <Header />

        {selectedDraft ? (
          <DraftDetail
            draft={selectedDraft}
            onBack={() => setSelectedDraftId(null)}
            onChange={refresh}
            user={user}
            showToast={showToast}
          />
        ) : (
          <DraftsList
            drafts={drafts}
            counts={counts}
            loading={loading}
            showCreateForm={showCreateForm}
            onShowCreate={() => setShowCreateForm(true)}
            onHideCreate={() => setShowCreateForm(false)}
            onCreated={(newDraftId) => { setShowCreateForm(false); setSelectedDraftId(newDraftId); refresh(); }}
            onSelect={setSelectedDraftId}
            user={user}
          />
        )}

        <div style={{ marginTop: '32px', fontFamily: FONT_MONO, fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>
          // {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'} · admin-only access · phase 6a (editable cards)
        </div>
      </div>

      <ToastStack toasts={toasts} />
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function Header() {
  return (
    <div style={{ marginBottom: '32px', paddingRight: '56px' }}>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '9px', letterSpacing: '4px', color: C.goldDim, marginBottom: '8px' }}>
        COLLECTION KEEPER · ADMIN
      </div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '1.5px', marginBottom: '6px' }}>
        SET INGESTION
      </div>
      <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.inkFaint, letterSpacing: '2px' }}>
        // UPLOAD A TOPPS PDF OR CSV. AI DRAFTS A CHECKLIST. YOU REVIEW AND PUBLISH. //
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: C.rowBg, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '14px 16px' }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: '10px', color: C.inkFaint, letterSpacing: '1px', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 700, color: color || '#fff' }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status, size = 'normal' }) {
  const color = STATUS_COLOR[status] || C.inkFaint;
  const label = STATUS_LABEL[status] || status?.toUpperCase() || 'UNKNOWN';
  const padding = size === 'large' ? '6px 12px' : '4px 10px';
  const fontSize = size === 'large' ? '10px' : '9px';
  return (
    <span style={{
      display: 'inline-block',
      padding,
      background: `${color}26`,
      border: `1px solid ${color}`,
      borderRadius: '3px',
      fontFamily: FONT_DISPLAY,
      fontSize,
      fontWeight: 700,
      letterSpacing: '2px',
      color,
    }}>
      {label}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Drafts list view
// ----------------------------------------------------------------------------

function DraftsList({ drafts, counts, loading, showCreateForm, onShowCreate, onHideCreate, onCreated, onSelect, user }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '32px' }}>
        <MetricCard label="DRAFTS" value={counts.total} />
        <MetricCard label="IN PROGRESS" value={counts.inProgress} color={C.cyan} />
        <MetricCard label="READY TO PUBLISH" value={counts.ready} color={C.gold} />
        <MetricCard label="PUBLISHED" value={counts.published} color={C.green} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: C.gold }}>DRAFTS</div>
        {!showCreateForm && (
          <button
            onClick={onShowCreate}
            style={{ fontFamily: FONT_DISPLAY, fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: C.bg, background: C.cyan, border: 0, borderRadius: '4px', padding: '10px 18px', cursor: 'pointer' }}
          >
            + NEW DRAFT
          </button>
        )}
      </div>

      {showCreateForm && (
        <CreateDraftForm onCancel={onHideCreate} onCreated={onCreated} user={user} />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: FONT_MONO, fontSize: '11px', color: C.cyan, letterSpacing: '2px' }}>
          // LOADING DRAFTS
        </div>
      ) : drafts.length === 0 ? (
        <div style={{ background: C.rowBg, border: `1px dashed ${C.border}`, borderRadius: '4px', padding: '32px', textAlign: 'center', fontFamily: FONT_MONO, fontSize: '12px', color: C.inkFaint, letterSpacing: '1px' }}>
          // NO DRAFTS YET · TAP "+ NEW DRAFT" TO START //
        </div>
      ) : (
        drafts.map(draft => (
          <DraftRow key={draft.id} draft={draft} onSelect={() => onSelect(draft.id)} />
        ))
      )}
    </>
  );
}

function DraftRow({ draft, onSelect }) {
  const accent = STATUS_COLOR[draft.status] || C.inkFaint;
  return (
    <div
      onClick={onSelect}
      style={{
        background: C.rowBg,
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: '4px',
        padding: '14px 18px',
        marginBottom: '8px',
        display: 'grid',
        gridTemplateColumns: '1fr 140px 120px 100px',
        gap: '16px',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(8,12,26,0.85)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = C.rowBg; }}
    >
      <div>
        <div style={{ fontSize: '15px', color: '#fff', marginBottom: '2px', fontWeight: 500 }}>{draft.set_name}</div>
        <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.inkFaint, letterSpacing: '1px' }}>
          {draft.set_slug} · {draft.set_year}
        </div>
      </div>
      <StatusBadge status={draft.status} />
      <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.inkSoft, letterSpacing: '1px' }}>{timeAgo(draft.updated_at)}</div>
      <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.cyan, letterSpacing: '1px', textAlign: 'right' }}>OPEN →</div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Create draft form
// ----------------------------------------------------------------------------

// Derive a CK-convention slug from the set name + year:
//   <year>-<descriptor>  e.g. "2025-masterwork", "2024-chrome-black".
// Strips "Topps", "Star Wars", apostrophes, the year (then re-prepends it),
// and any non-alnum chars. Matches the slug pattern used by every live set
// (2024-chrome-galaxy, 2025-hyperspace, 2025-smugglers-outpost, etc.).
function suggestSlug(name, year) {
  if (!name) return '';
  let s = name;
  if (year) s = s.replace(new RegExp(`\\b${year}\\b`, 'g'), '');
  s = s
    .toLowerCase()
    .replace(/topps/g, '')
    .replace(/star wars/g, '')
    .replace(/'/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return year ? `${year}-${s}`.replace(/-+$/, '') : s;
}

function CreateDraftForm({ onCancel, onCreated, user }) {
  const [setName, setSetName] = useState('');
  const [setYear, setSetYear] = useState('');
  const [setSlug, setSetSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Auto-suggest the slug from name+year while the user hasn't typed in the
  // slug field manually. The moment they edit slug, we stop auto-overwriting.
  useEffect(() => {
    if (slugManuallyEdited) return;
    const suggested = suggestSlug(setName, setYear);
    if (suggested !== setSlug) setSetSlug(suggested);
  }, [setName, setYear, slugManuallyEdited]);

  async function handleSubmit() {
    setError(null);
    if (!setName.trim()) { setError('Set name required.'); return; }
    const yearNum = parseInt(setYear, 10);
    if (!yearNum || yearNum < 1900 || yearNum > 2100) { setError('Year must be a valid 4-digit year.'); return; }
    if (!setSlug.trim()) { setError('Slug required (TraderCracks URL slug).'); return; }
    if (!file) { setError('PDF or CSV file required.'); return; }
    const ext = file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : file.name.toLowerCase().endsWith('.csv') ? 'csv' : null;
    if (!ext) { setError('File must be a .pdf or .csv'); return; }

    setSubmitting(true);
    try {
      const { data: draft, error: insertError } = await supabase
        .from('set_drafts')
        .insert({
          created_by: user.id,
          set_name: setName.trim(),
          set_year: yearNum,
          set_slug: setSlug.trim(),
          status: 'new',
        })
        .select()
        .single();
      if (insertError) throw insertError;

      const filePath = `${user.id}/${draft.id}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('set-pdfs')
        .upload(filePath, file, { contentType: file.type, upsert: false });
      if (uploadError) {
        await supabase.from('set_drafts').delete().eq('id', draft.id);
        throw uploadError;
      }

      const { error: updateError } = await supabase
        .from('set_drafts')
        .update({ pdf_path: filePath, updated_at: new Date().toISOString() })
        .eq('id', draft.id);
      if (updateError) throw updateError;

      onCreated(draft.id);
    } catch (err) {
      setError(err.message || 'Could not create draft.');
      setSubmitting(false);
    }
  }

  return (
    <div style={{ background: C.rowBgAlt, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.cyan}`, borderRadius: '4px', padding: '20px 22px', marginBottom: '14px' }}>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '12px', fontWeight: 700, letterSpacing: '2px', color: C.cyan, marginBottom: '14px' }}>NEW DRAFT</div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <FormField label="SET NAME" value={setName} onChange={setSetName} placeholder="2025 Topps Chrome Star Wars Galaxy" />
        <FormField label="YEAR" value={setYear} onChange={setSetYear} placeholder="2025" />
        <FormField label="SLUG (AUTO)" value={setSlug} onChange={v => { setSlugManuallyEdited(true); setSetSlug(v); }} placeholder="2025-chrome-galaxy" mono />
      </div>

      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: '10px', color: C.inkFaint, letterSpacing: '1px', marginBottom: '6px' }}>FILE (PDF OR CSV)</div>
        <input
          type="file"
          accept=".pdf,.csv,application/pdf,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ fontFamily: FONT_BODY, fontSize: '13px', color: C.ink }}
        />
      </div>

      {error && (
        <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.red, letterSpacing: '1px', marginBottom: '12px' }}>
          // ERROR: {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{ fontFamily: FONT_DISPLAY, fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: C.bg, background: submitting ? C.inkFaint : C.cyan, border: 0, borderRadius: '4px', padding: '10px 18px', cursor: submitting ? 'wait' : 'pointer' }}
        >
          {submitting ? 'CREATING...' : 'CREATE DRAFT'}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{ fontFamily: FONT_DISPLAY, fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: C.inkSoft, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '4px', padding: '10px 18px', cursor: 'pointer' }}
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, mono }) {
  return (
    <div>
      <div style={{ fontFamily: FONT_MONO, fontSize: '10px', color: C.inkFaint, letterSpacing: '1px', marginBottom: '6px' }}>{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: '3px',
          padding: '8px 10px',
          fontFamily: mono ? FONT_MONO : FONT_BODY,
          fontSize: '13px',
          color: C.ink,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Draft detail view
// ----------------------------------------------------------------------------

function DraftDetail({ draft, onBack, onChange, user, showToast }) {
  const [cards, setCards] = useState([]);
  const [parallels, setParallels] = useState([]);
  const [busy, setBusy] = useState({ parse: false, research: false, summary: false, publish: false });
  const [errors, setErrors] = useState({ parse: null, research: null, summary: null, publish: null });
  const [publishResult, setPublishResult] = useState(null);

  // Explicit pagination because Supabase Cloud's PostgREST `max-rows` setting
  // can cap a single .select() at 1000 rows even with .range() — silently
  // truncating big sets (2025 Chrome SW = 1,400+). Loop in 1000-page chunks
  // until we get a short page.
  const fetchAllByDraft = useCallback(async (table) => {
    const PAGE = 1000;
    const out = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table).select('*').eq('draft_id', draft.id).range(from, from + PAGE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      out.push(...data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    return out;
  }, [draft.id]);

  const loadStageData = useCallback(async () => {
    const [cards, parallels] = await Promise.all([
      fetchAllByDraft('set_draft_cards'),
      fetchAllByDraft('set_draft_parallels'),
    ]);
    const cRes = { data: cards };
    const pRes = { data: parallels };
    // Cards: natural sort (1, 2, ..., 100, AS-1, ..., A-AA)
    setCards((cRes.data || []).sort((a, b) =>
      String(a.card_number).localeCompare(String(b.card_number), undefined, { numeric: true })
    ));
    // Parallels: sort by sub_set first, then by print_run ascending (nulls first)
    setParallels((pRes.data || []).sort((a, b) => {
      const subSetCmp = String(a.sub_set || '').localeCompare(String(b.sub_set || ''));
      if (subSetCmp !== 0) return subSetCmp;
      // Within same sub_set: nulls (∞ unnumbered) first, then print_run ascending
      if (a.print_run === null && b.print_run === null) return 0;
      if (a.print_run === null) return -1;
      if (b.print_run === null) return 1;
      return a.print_run - b.print_run;
    }));
  }, [draft.id, fetchAllByDraft]);

  useEffect(() => {
    loadStageData();
  }, [loadStageData]);

  // ---- Card mutations (Phase 6A new) -----------------------------------
  const updateCard = useCallback(async (id, patch) => {
    const prev = cards.find(c => c.id === id);
    if (!prev) return;
    // Optimistic update with auto-flip to edited_by_human=true
    const next = { ...prev, ...patch, edited_by_human: true };
    setCards(arr => arr.map(c => c.id === id ? next : c));
    const { error } = await supabase
      .from('set_draft_cards')
      .update({ ...patch, edited_by_human: true })
      .eq('id', id);
    if (error) {
      // Rollback on error
      setCards(arr => arr.map(c => c.id === id ? prev : c));
      showToast(`SAVE FAILED: ${error.message}`, 'error');
    } else {
      showToast('SAVED', 'success');
    }
  }, [cards, showToast]);

  const addCard = useCallback(async () => {
    // Find next sensible card number (last numeric + 1, or 1)
    const lastNumeric = cards
      .map(c => parseInt(c.card_number, 10))
      .filter(n => !isNaN(n))
      .reduce((max, n) => Math.max(max, n), 0);
    const newCard = {
      draft_id: draft.id,
      card_number: String(lastNumeric + 1),
      card_name: '(new card)',
      sub_set: null,
      edited_by_human: true,
    };
    const { data, error } = await supabase
      .from('set_draft_cards')
      .insert(newCard)
      .select()
      .single();
    if (error) {
      showToast(`ADD FAILED: ${error.message}`, 'error');
      return;
    }
    setCards(arr => [...arr, data]);
    showToast('ROW ADDED', 'success');
  }, [cards, draft.id, showToast]);

  const deleteCard = useCallback(async (id) => {
    const card = cards.find(c => c.id === id);
    if (!card) return;
    if (!window.confirm(`Delete card ${card.card_number} "${card.card_name}"?`)) return;
    // Optimistic remove
    setCards(arr => arr.filter(c => c.id !== id));
    const { error } = await supabase.from('set_draft_cards').delete().eq('id', id);
    if (error) {
      // Rollback
      setCards(arr => [...arr, card].sort((a, b) =>
        String(a.card_number).localeCompare(String(b.card_number), undefined, { numeric: true })
      ));
      showToast(`DELETE FAILED: ${error.message}`, 'error');
    } else {
      showToast('ROW DELETED', 'success');
    }
  }, [cards, showToast]);
  // ----------------------------------------------------------------------

  // ---- Parallel mutations (Phase 6B new) -------------------------------
  const updateParallel = useCallback(async (id, patch) => {
    const prev = parallels.find(p => p.id === id);
    if (!prev) return;
    const next = { ...prev, ...patch, edited_by_human: true };
    setParallels(arr => arr.map(p => p.id === id ? next : p));
    const { error } = await supabase
      .from('set_draft_parallels')
      .update({ ...patch, edited_by_human: true })
      .eq('id', id);
    if (error) {
      setParallels(arr => arr.map(p => p.id === id ? prev : p));
      showToast(`SAVE FAILED: ${error.message}`, 'error');
    } else {
      showToast('SAVED', 'success');
    }
  }, [parallels, showToast]);

  const addParallel = useCallback(async (subSetValue) => {
    const newParallel = {
      draft_id: draft.id,
      sub_set: subSetValue,
      parallel_name: '(new parallel)',
      print_run: null,
      confidence: 'medium',
      source_url: null,
      edited_by_human: true,
    };
    const { data, error } = await supabase
      .from('set_draft_parallels')
      .insert(newParallel)
      .select()
      .single();
    if (error) {
      showToast(`ADD FAILED: ${error.message}`, 'error');
      return;
    }
    setParallels(arr => [...arr, data]);
    showToast('PARALLEL ADDED', 'success');
  }, [draft.id, showToast]);

  const deleteParallel = useCallback(async (id) => {
    const p = parallels.find(x => x.id === id);
    if (!p) return;
    const printRunDisplay = p.print_run === null ? '∞' : `/${p.print_run}`;
    if (!window.confirm(`Delete parallel "${p.parallel_name} ${printRunDisplay}" from "${p.sub_set || '—'}"?`)) return;
    setParallels(arr => arr.filter(x => x.id !== id));
    const { error } = await supabase.from('set_draft_parallels').delete().eq('id', id);
    if (error) {
      setParallels(arr => [...arr, p]);
      showToast(`DELETE FAILED: ${error.message}`, 'error');
    } else {
      showToast('PARALLEL DELETED', 'success');
    }
  }, [parallels, showToast]);
  // ----------------------------------------------------------------------

  // ---- Summary mutation (Phase 6B new) ---------------------------------
  const updateSummary = useCallback(async (newText) => {
    if (newText === draft.summary_draft) return; // no-op
    const { error } = await supabase
      .from('set_drafts')
      .update({ summary_draft: newText, summary_edited_by_human: true })
      .eq('id', draft.id);
    if (error) {
      showToast(`SAVE FAILED: ${error.message}`, 'error');
    } else {
      showToast('SUMMARY SAVED', 'success');
      onChange(); // bubble up so the draft prop refreshes with the new value
    }
  }, [draft.id, draft.summary_draft, showToast, onChange]);
  // ----------------------------------------------------------------------

  // ---- Stage 2 source config (source_type + URL override) --------------
  // The Stage 2 panel exposes both a source picker (TraderCracks / Beckett)
  // and an optional full-URL override. `tradercracks_slug_override` stays as
  // a legacy fallback — the edge function still honors it when source_type
  // is tradercracks and no URL override is set.
  const updateSourceConfig = useCallback(async (patch) => {
    // patch: { source_type?, source_url_override?, tradercracks_slug_override? }
    const next = {};
    if ('source_type' in patch) next.source_type = patch.source_type;
    if ('source_url_override' in patch) {
      const trimmed = (patch.source_url_override || '').trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed)) {
        showToast('URL override must start with http:// or https://', 'error');
        return;
      }
      next.source_url_override = trimmed || null;
    }
    if ('tradercracks_slug_override' in patch) {
      const trimmed = (patch.tradercracks_slug_override || '').trim().toLowerCase();
      if (trimmed && !/^[a-z0-9-]+$/.test(trimmed)) {
        showToast('TC slug: lowercase letters, digits, hyphens only', 'error');
        return;
      }
      next.tradercracks_slug_override = trimmed || null;
    }
    if (Object.keys(next).length === 0) return;
    const { error } = await supabase
      .from('set_drafts')
      .update(next)
      .eq('id', draft.id);
    if (error) {
      showToast(`SAVE FAILED: ${error.message}`, 'error');
    } else {
      showToast('SOURCE SAVED', 'success');
      onChange();
    }
  }, [draft.id, showToast, onChange]);
  // ----------------------------------------------------------------------

  async function runStage(stageName, fnName) {
    setBusy(b => ({ ...b, [stageName]: true }));
    setErrors(e => ({ ...e, [stageName]: null }));
    try {
      const { data, error } = await supabase.functions.invoke(fnName, {
        body: { draft_id: draft.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await loadStageData();
      onChange();
    } catch (err) {
      setErrors(e => ({ ...e, [stageName]: err.message || 'Stage failed.' }));
    } finally {
      setBusy(b => ({ ...b, [stageName]: false }));
    }
  }

  // Generate the src/sets/<slug>.js scaffold via pipeline-publish. Unlike the
  // stages, this writes nothing — it returns a file to review, finish, and
  // commit. Result opens in a modal.
  async function runPublish() {
    setBusy(b => ({ ...b, publish: true }));
    setErrors(e => ({ ...e, publish: null }));
    try {
      const { data, error } = await supabase.functions.invoke('pipeline-publish', {
        body: { draft_id: draft.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setPublishResult(data);
    } catch (err) {
      setErrors(e => ({ ...e, publish: err.message || 'Publish failed.' }));
    } finally {
      setBusy(b => ({ ...b, publish: false }));
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete draft "${draft.set_name}"? This wipes the draft, its cards, parallels, and uploaded file. Cannot be undone.`)) return;
    try {
      if (draft.pdf_path) {
        await supabase.storage.from('set-pdfs').remove([draft.pdf_path]);
      }
      const { error } = await supabase.from('set_drafts').delete().eq('id', draft.id);
      if (error) throw error;
      onBack();
      onChange();
    } catch (err) {
      window.alert('Could not delete: ' + (err.message || 'unknown error'));
    }
  }

  return (
    <>
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={onBack}
          style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.cyan, background: 'transparent', border: 0, padding: '4px 0', cursor: 'pointer', letterSpacing: '1px' }}
        >
          ← BACK TO DRAFTS
        </button>
      </div>

      <div style={{ background: C.rowBgAlt, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '22px', marginBottom: '12px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '18px', fontWeight: 700, color: '#fff', letterSpacing: '1px', marginBottom: '6px', textTransform: 'uppercase' }}>{draft.set_name}</div>
            <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.inkSoft, letterSpacing: '1px' }}>
              DRAFT_ID: {draft.id} · {draft.set_year} · slug: {draft.set_slug}
            </div>
          </div>
          <StatusBadge status={draft.status} size="large" />
        </div>

        <StagePanel
          stageNum={1}
          title="CARDS"
          accent={C.cyan}
          subtitle={cards.length > 0 ? `Extracted from PDF/CSV. ${cards.length} cards${cards.filter(c => c.edited_by_human).length > 0 ? ` · ${cards.filter(c => c.edited_by_human).length} edited` : ''}.` : 'Extracted from PDF/CSV. Not yet parsed.'}
          buttonLabel={cards.length > 0 ? 'RE-RUN ↻' : 'RUN PARSE →'}
          buttonStyle={cards.length > 0 ? 'outline' : 'solid'}
          buttonColor={cards.length > 0 ? C.gold : C.cyan}
          onRun={() => runStage('parse', 'pipeline-parse-pdf')}
          busy={busy.parse}
          error={errors.parse}
          empty={cards.length === 0}
          emptyText="// NO CARDS YET · TAP RUN PARSE TO EXTRACT FROM PDF/CSV //"
        >
          {cards.length > 0 && (
            <CardsTable
              cards={cards}
              onUpdate={updateCard}
              onAdd={addCard}
              onDelete={deleteCard}
            />
          )}
        </StagePanel>

        <StagePanel
          stageNum={2}
          title="PARALLELS"
          accent={C.gold}
          subtitle={parallels.length > 0 ? `From ${sourceLabel(draft.source_type)}. ${parallels.length} parallels${parallels.filter(p => p.edited_by_human).length > 0 ? ` · ${parallels.filter(p => p.edited_by_human).length} edited` : ''}.` : `From ${sourceLabel(draft.source_type)}. Not yet researched.`}
          buttonLabel={parallels.length > 0 ? 'RE-RUN ↻' : 'RUN RESEARCH →'}
          buttonStyle={parallels.length > 0 ? 'outline' : 'solid'}
          buttonColor={parallels.length > 0 ? C.gold : C.cyan}
          onRun={() => runStage('research', 'pipeline-research-parallels')}
          busy={busy.research}
          error={errors.research}
          empty={parallels.length === 0}
          emptyText={`// NO PARALLELS YET · TAP RUN RESEARCH TO FETCH FROM ${sourceLabel(draft.source_type).toUpperCase()} //`}
          subHeader={
            <SourcePickerInput
              draft={draft}
              onSave={updateSourceConfig}
            />
          }
        >
          {parallels.length > 0 && (
            <ParallelsTable
              parallels={parallels}
              onUpdate={updateParallel}
              onAdd={addParallel}
              onDelete={deleteParallel}
            />
          )}
        </StagePanel>

        <StagePanel
          stageNum={3}
          title="SUMMARY"
          accent={C.green}
          subtitle={
            draft.summary_draft
              ? `Resources page copy. Drafted${draft.summary_edited_by_human ? ' · edited' : ''}.`
              : 'Resources page copy. Not yet drafted.'
          }
          buttonLabel={draft.summary_draft ? 'RE-RUN ↻' : 'RUN DRAFT →'}
          buttonStyle={draft.summary_draft ? 'outline' : 'solid'}
          buttonColor={draft.summary_draft ? C.gold : C.cyan}
          onRun={() => runStage('summary', 'pipeline-draft-summary')}
          busy={busy.summary}
          error={errors.summary}
          empty={!draft.summary_draft}
          emptyText="// NO SUMMARY YET · TAP RUN DRAFT TO GENERATE //"
        >
          {draft.summary_draft && (
            <SummaryEditor
              initialValue={draft.summary_draft}
              edited={!!draft.summary_edited_by_human}
              onSave={updateSummary}
            />
          )}
        </StagePanel>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: `1px solid ${C.border}` }}>
          <button
            onClick={handleDelete}
            style={{ fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: C.red, background: 'transparent', border: `1px solid ${C.red}`, borderRadius: '4px', padding: '8px 14px', cursor: 'pointer' }}
          >
            DELETE DRAFT
          </button>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {errors.publish && (
              <div style={{ fontFamily: FONT_MONO, fontSize: '10px', color: C.red, letterSpacing: '1px', maxWidth: '320px' }}>// {errors.publish}</div>
            )}
            <div style={{ fontFamily: FONT_MONO, fontSize: '10px', color: C.inkFaint, letterSpacing: '1px' }}>// GENERATES A SET FILE TO REVIEW + COMMIT //</div>
            <button
              onClick={runPublish}
              disabled={busy.publish || cards.length === 0}
              title={cards.length === 0 ? 'Parse cards first' : 'Generate the src/sets file scaffold'}
              style={{ fontFamily: FONT_DISPLAY, fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: cards.length === 0 ? C.inkFaint : C.bg, background: cards.length === 0 ? 'transparent' : (busy.publish ? C.inkFaint : C.purple), border: cards.length === 0 ? `1px solid ${C.border}` : 0, borderRadius: '4px', padding: '10px 18px', cursor: (busy.publish || cards.length === 0) ? 'not-allowed' : 'pointer' }}
            >
              {busy.publish ? 'GENERATING...' : 'PUBLISH ✓'}
            </button>
          </div>
        </div>

      </div>

      {publishResult && (
        <PublishModal result={publishResult} onClose={() => setPublishResult(null)} showToast={showToast} />
      )}
    </>
  );
}

function PublishModal({ result, onClose, showToast }) {
  const { file_name, file_content, registry_entry, stats, warnings } = result;
  const [edited, setEdited] = useState(file_content);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const hasTodo = /#TODO/.test(edited) || /#TODO/.test(registry_entry || '');

  async function commit() {
    setCommitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('pipeline-commit-set', {
        body: { draft_id: result.draft_id, file_content: edited, registry_entry },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCommitted(data);
      showToast('COMMITTED TO MAIN', 'success');
    } catch (err) {
      showToast(`COMMIT FAILED: ${err.message || 'error'}`, 'error');
    } finally {
      setCommitting(false);
    }
  }

  function download() {
    const blob = new Blob([file_content], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (file_name || 'set.js').split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('FILE DOWNLOADED', 'success');
  }

  async function copy(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} COPIED`, 'success');
    } catch (_e) {
      showToast('COPY FAILED — SELECT AND COPY MANUALLY', 'error');
    }
  }

  const btn = (color, solid) => ({
    fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700, letterSpacing: '2px',
    color: solid ? C.bg : color, background: solid ? color : 'transparent',
    border: solid ? 0 : `1px solid ${color}`, borderRadius: '4px', padding: '8px 14px', cursor: 'pointer',
  });

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '22px', width: '100%', maxWidth: '820px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '15px', fontWeight: 700, color: C.purple, letterSpacing: '2px', marginBottom: '4px' }}>GENERATED SET FILE</div>
            <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.inkSoft }}>{file_name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, color: C.inkSoft, fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ fontFamily: FONT_BODY, fontSize: '13px', color: C.ink, marginBottom: '10px' }}>
          Colors, types and the registry entry are filled in. Review, resolve any TODO in the box below, then tap COMMIT TO MAIN to publish (or download and commit by hand). The set goes live ~1-2 min after committing.
        </div>

        {stats && (
          <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.inkSoft, letterSpacing: '0.5px', marginBottom: '8px' }}>
            {stats.sub_set_count} sub-sets · {stats.card_count} cards · {stats.parallel_count} parallels · {stats.color_todo_count} color TODOs{stats.unplaced_parallel_count > 0 ? ` · ${stats.unplaced_parallel_count} unplaced` : ''}
          </div>
        )}

        {Array.isArray(warnings) && warnings.length > 0 && (
          <div style={{ background: C.rowBg, border: `1px solid ${C.goldDim}`, borderRadius: '4px', padding: '10px 12px', marginBottom: '10px' }}>
            {warnings.map((w, i) => (
              <div key={i} style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.gold, letterSpacing: '0.5px', marginBottom: i < warnings.length - 1 ? '4px' : 0 }}>// {w}</div>
            ))}
          </div>
        )}

        <textarea
          value={edited}
          onChange={e => setEdited(e.target.value)}
          spellCheck={false}
          style={{ flex: 1, minHeight: '300px', width: '100%', boxSizing: 'border-box', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px', fontFamily: FONT_MONO, fontSize: '11px', color: C.ink, lineHeight: 1.5, resize: 'vertical', marginBottom: '12px' }}
        />

        {committed ? (
          <div style={{ background: C.rowBg, border: `1px solid ${C.green}`, borderRadius: '4px', padding: '12px 14px', marginBottom: '10px' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: C.green, marginBottom: '6px' }}>COMMITTED TO MAIN ✓</div>
            <div style={{ fontFamily: FONT_BODY, fontSize: '13px', color: C.ink, marginBottom: '6px' }}>
              {committed.deploy_note}{committed.registry_committed ? '' : ' Registry was NOT auto-updated — see warnings.'}
            </div>
            {Array.isArray(committed.warnings) && committed.warnings.map((w, i) => (
              <div key={i} style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.gold, marginBottom: '3px' }}>// {w}</div>
            ))}
            {committed.set_commit_url && (
              <a href={committed.set_commit_url} target="_blank" rel="noreferrer" style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.cyan }}>View commit ↗</a>
            )}
          </div>
        ) : hasTodo && (
          <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.gold, letterSpacing: '0.5px', marginBottom: '6px' }}>// Resolve every #TODO (in the box above and the registry entry) before committing.</div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {!committed && (
            <button onClick={commit} disabled={committing || hasTodo} style={{ ...btn(C.green, true), opacity: (committing || hasTodo) ? 0.5 : 1, cursor: (committing || hasTodo) ? 'not-allowed' : 'pointer' }}>
              {committing ? 'COMMITTING...' : 'COMMIT TO MAIN'}
            </button>
          )}
          <button onClick={download} style={btn(C.purple, true)}>DOWNLOAD .JS FILE</button>
          <button onClick={() => copy(edited, 'FILE')} style={btn(C.cyan, false)}>COPY FILE</button>
          <button onClick={() => copy(registry_entry, 'REGISTRY ENTRY')} style={btn(C.gold, false)}>COPY REGISTRY ENTRY</button>
          <button onClick={onClose} style={{ ...btn(C.inkSoft, false), marginLeft: 'auto' }}>{committed ? 'DONE' : 'CLOSE'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Stage 2 source helpers
// ============================================================================
function sourceLabel(sourceType) {
  switch ((sourceType || 'tradercracks').toLowerCase()) {
    case 'beckett': return 'Beckett';
    case 'tradercracks':
    default: return 'TraderCracks';
  }
}

// Derive the URL the edge function would auto-build for a given source. Kept
// in sync with resolveSourceUrl() in pipeline-research-parallels/index.ts so
// the UI preview matches what actually gets fetched.
function deriveSourceUrl(draft) {
  const sourceType = (draft.source_type || 'tradercracks').toLowerCase();
  const year = draft.set_year;
  const slug = draft.set_slug || '';
  const yearPrefix = `${year}-`;
  const slugPart = slug.startsWith(yearPrefix) ? slug.slice(yearPrefix.length) : slug;
  if (sourceType === 'tradercracks') {
    const tcSlug = (draft.tradercracks_slug_override || '').trim() || `${year}-${slugPart}`;
    return `https://www.tradercracks.com/${tcSlug}-trading-cards-checklist`;
  }
  if (sourceType === 'beckett') {
    const beckettSlug = slugPart.replace(/^topps-/, '').replace(/^star-wars-/, '');
    return `https://www.beckett.com/news/${year}-topps-star-wars-${beckettSlug}/`;
  }
  return '';
}

// ============================================================================
// SourcePickerInput — Stage 2 source picker + URL override.
// Source dropdown (TraderCracks / Beckett) + an optional full-URL override.
// For TraderCracks specifically, also keeps the legacy slug override input
// since most existing drafts use it. Saves on blur / change.
// ============================================================================
function SourcePickerInput({ draft, onSave }) {
  const sourceType = (draft.source_type || 'tradercracks').toLowerCase();
  const [urlValue, setUrlValue] = useState(draft.source_url_override || '');
  const [tcSlugValue, setTcSlugValue] = useState(draft.tradercracks_slug_override || '');
  useEffect(() => { setUrlValue(draft.source_url_override || ''); }, [draft.source_url_override]);
  useEffect(() => { setTcSlugValue(draft.tradercracks_slug_override || ''); }, [draft.tradercracks_slug_override]);

  const effectiveDraft = {
    ...draft,
    source_url_override: urlValue || null,
    tradercracks_slug_override: tcSlugValue || null,
  };
  const previewUrl = (urlValue || '').trim() || deriveSourceUrl(effectiveDraft);

  const inputStyle = {
    fontFamily: FONT_MONO, fontSize: '11px', letterSpacing: '1px',
    color: C.ink, background: C.rowBg, border: `1px solid ${C.border}`,
    borderRadius: '3px', padding: '4px 8px',
  };
  const labelStyle = { color: C.inkFaint };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontFamily: FONT_MONO, fontSize: '11px', color: C.inkFaint, letterSpacing: '1px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={labelStyle}>SOURCE:</span>
        <select
          value={sourceType}
          onChange={(e) => onSave({ source_type: e.target.value })}
          style={{ ...inputStyle, minWidth: '140px' }}
        >
          <option value="tradercracks">TraderCracks</option>
          <option value="beckett">Beckett</option>
        </select>
        {sourceType === 'tradercracks' && (
          <>
            <span style={labelStyle}>TC_SLUG:</span>
            <input
              type="text"
              value={tcSlugValue}
              placeholder={`(blank → uses ${draft.set_year}-${(draft.set_slug || '').replace(`${draft.set_year}-`, '')})`}
              onChange={(e) => setTcSlugValue(e.target.value)}
              onBlur={() => onSave({ tradercracks_slug_override: tcSlugValue })}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              spellCheck={false}
              style={{ ...inputStyle, minWidth: '280px', flex: '0 1 auto' }}
            />
          </>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={labelStyle}>URL_OVERRIDE:</span>
        <input
          type="text"
          value={urlValue}
          placeholder="(optional full URL — overrides auto-derived URL)"
          onChange={(e) => setUrlValue(e.target.value)}
          onBlur={() => onSave({ source_url_override: urlValue })}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          spellCheck={false}
          style={{ ...inputStyle, minWidth: '460px', flex: '1 1 auto' }}
        />
      </div>
      <div style={{ color: C.inkFaint, paddingLeft: '2px' }}>
        → {previewUrl.replace(/^https?:\/\//, '')}
      </div>
    </div>
  );
}

function StagePanel({ stageNum, title, accent, subtitle, buttonLabel, buttonStyle, buttonColor, onRun, busy, error, empty, emptyText, children, subHeader }) {
  const isOutline = buttonStyle === 'outline';
  return (
    <div style={{ background: C.rowBg, border: `1px solid ${C.border}`, borderLeft: `3px solid ${accent}`, borderRadius: '4px', padding: '18px 20px', marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: '12px', fontWeight: 700, letterSpacing: '2px', color: accent, marginBottom: '4px' }}>STAGE {stageNum} · {title}</div>
          <div style={{ fontSize: '13px', color: C.inkSoft }}>{subtitle}</div>
        </div>
        <button
          onClick={onRun}
          disabled={busy}
          style={
            isOutline
              ? { fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: buttonColor, background: 'transparent', border: `1px solid ${buttonColor}`, borderRadius: '4px', padding: '7px 13px', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.5 : 1 }
              : { fontFamily: FONT_DISPLAY, fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: C.bg, background: busy ? C.inkFaint : buttonColor, border: 0, borderRadius: '4px', padding: '8px 14px', cursor: busy ? 'wait' : 'pointer' }
          }
        >
          {busy ? 'RUNNING...' : buttonLabel}
        </button>
      </div>
      {subHeader && (
        <div style={{ marginBottom: '10px' }}>{subHeader}</div>
      )}
      {error && (
        <div style={{ fontFamily: FONT_MONO, fontSize: '11px', color: C.red, letterSpacing: '1px', marginBottom: '10px' }}>
          // ERROR: {error}
        </div>
      )}
      {empty ? (
        <div style={{ background: 'rgba(74,90,122,0.08)', border: `1px dashed ${C.border}`, borderRadius: '4px', padding: '14px', fontFamily: FONT_MONO, fontSize: '11px', color: C.inkFaint, letterSpacing: '1px', textAlign: 'center' }}>
          {emptyText}
        </div>
      ) : children}
    </div>
  );
}

// ============================================================================
// CardsTable — editable (Phase 6A new)
// ============================================================================

function CardsTable({ cards, onUpdate, onAdd, onDelete }) {
  return (
    <div>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px', maxHeight: '480px', overflowY: 'auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '90px 1fr 130px 32px',
          gap: '8px',
          padding: '8px 14px',
          borderBottom: `1px solid ${C.border}`,
          fontFamily: FONT_MONO,
          fontSize: '9px',
          letterSpacing: '1px',
          color: C.inkFaint,
          position: 'sticky',
          top: 0,
          background: C.bg,
          zIndex: 1,
        }}>
          <div>NUMBER</div>
          <div>NAME</div>
          <div>SUB-SET</div>
          <div></div>
        </div>
        {cards.map(card => (
          <CardRow
            key={card.id}
            card={card}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '10px' }}>
        <button
          onClick={onAdd}
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '2px',
            color: C.cyan,
            background: 'transparent',
            border: `1px dashed ${C.cyan}`,
            borderRadius: '4px',
            padding: '7px 13px',
            cursor: 'pointer',
          }}
        >
          + ADD CARD
        </button>
      </div>
    </div>
  );
}

function CardRow({ card, onUpdate, onDelete }) {
  // Local state per field — keeps the input responsive while typing
  const [number, setNumber] = useState(card.card_number);
  const [name, setName] = useState(card.card_name);
  const [subSet, setSubSet] = useState(card.sub_set || '');

  // Sync local state when card prop changes (e.g. after a re-fetch)
  useEffect(() => { setNumber(card.card_number); }, [card.card_number]);
  useEffect(() => { setName(card.card_name); }, [card.card_name]);
  useEffect(() => { setSubSet(card.sub_set || ''); }, [card.sub_set]);

  function commit(field, value, originalValue) {
    if (value === originalValue) return;  // no-op if unchanged
    if (field === 'card_number' && !value.trim()) {
      // Required field — revert
      setNumber(originalValue);
      return;
    }
    if (field === 'card_name' && !value.trim()) {
      // Required field — revert
      setName(originalValue);
      return;
    }
    const patch = field === 'sub_set'
      ? { sub_set: value.trim() === '' ? null : value.trim() }
      : { [field]: value.trim() };
    onUpdate(card.id, patch);
  }

  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '2px',
    padding: '4px 6px',
    fontFamily: FONT_BODY,
    fontSize: '13px',
    color: C.ink,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.1s ease, background 0.1s ease',
  };

  const monoInputStyle = { ...inputStyle, fontFamily: FONT_MONO, color: C.inkSoft };

  function handleFocus(e) {
    e.target.style.borderColor = C.border;
    e.target.style.background = C.cardAlt;
  }
  function handleBlur(e) {
    e.target.style.borderColor = 'transparent';
    e.target.style.background = 'transparent';
  }

  const editedIndicator = card.edited_by_human ? (
    <span title="Edited by human" style={{
      display: 'inline-block',
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      background: C.gold,
      marginLeft: '6px',
      verticalAlign: 'middle',
    }} />
  ) : null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '90px 1fr 130px 32px',
      gap: '8px',
      padding: '6px 14px',
      borderBottom: `1px solid ${C.borderSoft}`,
      alignItems: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onFocus={handleFocus}
          onBlur={(e) => { handleBlur(e); commit('card_number', number, card.card_number); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          style={monoInputStyle}
        />
        {editedIndicator}
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onFocus={handleFocus}
        onBlur={(e) => { handleBlur(e); commit('card_name', name, card.card_name); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        style={inputStyle}
      />
      <input
        type="text"
        value={subSet}
        onChange={(e) => setSubSet(e.target.value)}
        onFocus={handleFocus}
        onBlur={(e) => { handleBlur(e); commit('sub_set', subSet, card.sub_set || ''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder="—"
        style={{ ...monoInputStyle, fontSize: '11px' }}
      />
      <button
        onClick={() => onDelete(card.id)}
        title="Delete row"
        style={{
          background: 'transparent',
          border: 'none',
          color: C.inkFaint,
          fontSize: '16px',
          cursor: 'pointer',
          padding: '0',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '3px',
          transition: 'color 0.1s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = C.red; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = C.inkFaint; }}
      >
        ×
      </button>
    </div>
  );
}

// ============================================================================
// ============================================================================
// ParallelsTable — editable, grouped by sub-set (Phase 6B)
// ============================================================================

function ParallelsTable({ parallels, onUpdate, onAdd, onDelete }) {
  // Group parallels by sub_set (preserving the sort order from loadStageData)
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of parallels) {
      const key = p.sub_set || '— No sub-set —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()); // [[subSetName, [parallels...]], ...]
  }, [parallels]);

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '3px', maxHeight: '520px', overflowY: 'auto' }}>
      {groups.map(([subSetName, group]) => {
        const editedCount = group.filter(p => p.edited_by_human).length;
        return (
          <div key={subSetName}>
            {/* Section header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px 8px',
              borderBottom: `1px solid ${C.border}`,
              borderTop: `1px solid ${C.border}`,
              background: C.cardAlt,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: C.gold, textTransform: 'uppercase' }}>
                {subSetName}
                <span style={{ marginLeft: '10px', color: C.inkFaint, fontWeight: 400, fontSize: '10px' }}>· {group.length}{editedCount > 0 ? ` · ${editedCount} edited` : ''}</span>
              </div>
              <button
                onClick={() => onAdd(subSetName === '— No sub-set —' ? null : subSetName)}
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '1.5px',
                  color: C.cyan,
                  background: 'transparent',
                  border: `1px dashed ${C.cyan}`,
                  borderRadius: '3px',
                  padding: '4px 9px',
                  cursor: 'pointer',
                }}
              >
                + ADD
              </button>
            </div>
            {/* Column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 80px 100px 32px',
              gap: '8px',
              padding: '6px 14px',
              borderBottom: `1px solid ${C.borderSoft}`,
              fontFamily: FONT_MONO,
              fontSize: '9px',
              letterSpacing: '1px',
              color: C.inkFaint,
            }}>
              <div>PARALLEL</div>
              <div style={{ textAlign: 'right' }}>PRINT RUN</div>
              <div style={{ textAlign: 'right' }}>CONFIDENCE</div>
              <div></div>
            </div>
            {/* Rows */}
            {group.map(p => (
              <ParallelRow
                key={p.id}
                parallel={p}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ParallelRow({ parallel, onUpdate, onDelete }) {
  const [name, setName] = useState(parallel.parallel_name);
  // Print run is stored as integer or null. Edit as text so users can clear it.
  const [printRunText, setPrintRunText] = useState(
    parallel.print_run === null || parallel.print_run === undefined ? '' : String(parallel.print_run)
  );
  const [confidence, setConfidence] = useState(parallel.confidence || 'medium');

  useEffect(() => { setName(parallel.parallel_name); }, [parallel.parallel_name]);
  useEffect(() => {
    setPrintRunText(parallel.print_run === null || parallel.print_run === undefined ? '' : String(parallel.print_run));
  }, [parallel.print_run]);
  useEffect(() => { setConfidence(parallel.confidence || 'medium'); }, [parallel.confidence]);

  function commitName() {
    const trimmed = name.trim();
    if (!trimmed) {
      setName(parallel.parallel_name); // revert empty
      return;
    }
    if (trimmed === parallel.parallel_name) return;
    onUpdate(parallel.id, { parallel_name: trimmed });
  }

  function commitPrintRun() {
    const trimmed = printRunText.trim();
    let newValue = null;
    if (trimmed !== '') {
      const n = parseInt(trimmed, 10);
      if (!isNaN(n) && n > 0) {
        newValue = n;
      } else {
        // Invalid input — revert
        setPrintRunText(parallel.print_run === null || parallel.print_run === undefined ? '' : String(parallel.print_run));
        return;
      }
    }
    if (newValue === parallel.print_run) return;
    onUpdate(parallel.id, { print_run: newValue });
  }

  function commitConfidence(newConfidence) {
    if (newConfidence === parallel.confidence) return;
    onUpdate(parallel.id, { confidence: newConfidence });
  }

  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '2px',
    padding: '4px 6px',
    fontFamily: FONT_BODY,
    fontSize: '13px',
    color: C.ink,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.1s ease, background 0.1s ease',
  };
  const monoInputStyle = { ...inputStyle, fontFamily: FONT_MONO, color: C.inkSoft, textAlign: 'right' };

  function handleFocus(e) {
    e.target.style.borderColor = C.border;
    e.target.style.background = C.cardAlt;
  }
  function handleBlur(e) {
    e.target.style.borderColor = 'transparent';
    e.target.style.background = 'transparent';
  }

  const editedIndicator = parallel.edited_by_human ? (
    <span title="Edited by human" style={{
      display: 'inline-block',
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      background: C.gold,
      marginLeft: '6px',
      verticalAlign: 'middle',
    }} />
  ) : null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 80px 100px 32px',
      gap: '8px',
      padding: '6px 14px',
      borderBottom: `1px solid ${C.borderSoft}`,
      alignItems: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={handleFocus}
          onBlur={(e) => { handleBlur(e); commitName(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          style={inputStyle}
        />
        {editedIndicator}
      </div>
      <input
        type="text"
        value={printRunText}
        onChange={(e) => setPrintRunText(e.target.value)}
        onFocus={handleFocus}
        onBlur={(e) => { handleBlur(e); commitPrintRun(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder="∞"
        style={monoInputStyle}
      />
      <select
        value={confidence}
        onChange={(e) => { setConfidence(e.target.value); commitConfidence(e.target.value); }}
        style={{
          width: '100%',
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: '2px',
          padding: '4px 6px',
          fontFamily: FONT_DISPLAY,
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1px',
          color: confidence === 'high' ? C.green : confidence === 'medium' ? C.gold : C.red,
          outline: 'none',
          cursor: 'pointer',
          textAlign: 'right',
        }}
      >
        <option value="high">HIGH</option>
        <option value="medium">MEDIUM</option>
        <option value="low">LOW</option>
      </select>
      <button
        onClick={() => onDelete(parallel.id)}
        title="Delete row"
        style={{
          background: 'transparent',
          border: 'none',
          color: C.inkFaint,
          fontSize: '16px',
          cursor: 'pointer',
          padding: '0',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '3px',
          transition: 'color 0.1s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = C.red; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = C.inkFaint; }}
      >
        ×
      </button>
    </div>
  );
}

// ============================================================================
// SummaryEditor — editable textarea (Phase 6B)
// ============================================================================

function SummaryEditor({ initialValue, edited, onSave }) {
  const [text, setText] = useState(initialValue);

  // Sync local state when initialValue changes (e.g. after Re-Run)
  useEffect(() => { setText(initialValue); }, [initialValue]);

  const wordCount = useMemo(() => {
    const trimmed = (text || '').trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [text]);

  function handleBlur() {
    onSave(text);
  }

  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        style={{
          width: '100%',
          minHeight: '180px',
          maxHeight: '480px',
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: '3px',
          padding: '14px 16px',
          fontFamily: FONT_BODY,
          fontSize: '14px',
          lineHeight: 1.6,
          color: C.ink,
          outline: 'none',
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '8px',
        fontFamily: FONT_MONO,
        fontSize: '10px',
        letterSpacing: '1px',
        color: C.inkFaint,
      }}>
        <div>{wordCount} words</div>
        <div style={{ color: edited ? C.gold : C.inkFaint }}>
          {edited ? '● EDITED BY HUMAN' : '○ AS DRAFTED'}
        </div>
      </div>
    </div>
  );
}

function ConfidencePill({ confidence }) {
  const color = confidence === 'high' ? C.green : confidence === 'medium' ? C.gold : C.red;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      background: `${color}1f`,
      color,
      fontFamily: FONT_DISPLAY,
      fontSize: '8px',
      fontWeight: 700,
      letterSpacing: '1px',
      borderRadius: '2px',
    }}>
      {(confidence || 'unknown').toUpperCase()}
    </span>
  );
}
